import CloseSmallIcon from 'assets/icons/close-small-icon.svg';
import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import {
  DESKTOP_MIN_WIDTH,
  STATUS_CANCELLED,
  STATUS_COMPLETED,
  STATUS_DELIVERING,
  STATUS_ON_HOLD,
  STATUS_PROCESSING,
  STATUS_SUBMITTED,
  TRANSACTION_STATUS_TRANSLATE_REFERENCE,
} from 'constants';
import { searchTextValid } from 'helpers';
import { Fade } from 'helpers/animation';
import { checkMaintenance } from 'helpers/maintenance';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { arrayUtils } from 'utils';
import FormInputCheckbox from '../FormInputCheckbox';

const initStatusDropdown = [
  { id: STATUS_SUBMITTED, name: STATUS_SUBMITTED, code: STATUS_SUBMITTED },
  {
    id: STATUS_DELIVERING,
    name: STATUS_DELIVERING,
    code: STATUS_DELIVERING,
  },
  {
    id: STATUS_PROCESSING,
    name: STATUS_PROCESSING,
    code: STATUS_PROCESSING,
  },
  { id: STATUS_COMPLETED, name: STATUS_COMPLETED, code: STATUS_COMPLETED },
  { id: STATUS_CANCELLED, name: STATUS_CANCELLED, code: STATUS_CANCELLED },
  { id: STATUS_ON_HOLD, name: STATUS_ON_HOLD, code: STATUS_ON_HOLD },
];

const FILTER_STATUS_SEARCH_NAME = 'filter-status-search';

const CustomFilterStatusDropdown = ({ filtered, setFiltered }) => {
  const { t, isVi } = useLang();
  const { statusSelected } = filtered;

  const [status, setStatus] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [statusSearch, setStatusSearch] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const isSearchInvalid = !isVi && !searchTextValid(searchValue);

  const fetchTransactionsStatus = async () => {
    try {
      setStatus(initStatusDropdown);
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };

  useEffect(() => {
    fetchTransactionsStatus();
  }, []);

  useInputFocus(FILTER_STATUS_SEARCH_NAME);

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchValue(value);

    if (!value) {
      setStatusSearch(status);

      return;
    }

    const newStatusSearch = status?.filter((status) =>
      isVi
        ? (t(TRANSACTION_STATUS_TRANSLATE_REFERENCE[status.name])
            ?.toLowerCase()
            ?.includes(value?.toLowerCase()) &&
            status) ||
          (status?.name?.toLowerCase()?.includes(value?.toLowerCase()) &&
            status)
        : status?.name?.toLowerCase()?.includes(value?.toLowerCase()) && status,
    );
    setStatusSearch(newStatusSearch);
  };

  const handleStatusSelected = (e, status) => {
    e.preventDefault();
    e.stopPropagation();

    let newStatusSelected = statusSelected ? [...statusSelected] : [];

    if (
      newStatusSelected?.some(
        (statusSelected) => statusSelected.code === status.code,
      )
    ) {
      newStatusSelected = statusSelected?.filter(
        (statusSelected) => statusSelected.code !== status.code,
      );
    } else {
      newStatusSelected.push(status);
    }

    const newFiltered = {
      ...filtered,
      statusSelected: newStatusSelected,
    };
    setFiltered(newFiltered);

    setSearchValue('');

    setStatusSearch(null);
  };

  const handleDropdownOnToggle = () => {
    setSearchValue('');
    setStatusSearch(status);
    setShowDropdown(!isShowDropdown);
  };

  const handleDeleteStatusSelected = (e, status) => {
    e.preventDefault();
    e.stopPropagation();

    const newStatusSelected = statusSelected?.filter(
      (statusSelected) => statusSelected.code !== status.code,
    );

    const newFiltered = {
      ...filtered,
      statusSelected: newStatusSelected,
    };
    setFiltered(newFiltered);
  };

  const renderDropdownToggleChildren = () => (
    <>
      {!arrayUtils.isArrayEmpty(statusSelected)
        ? statusSelected.map((status) => (
            <Pill key={status.code}>
              {t(TRANSACTION_STATUS_TRANSLATE_REFERENCE[status.name])}{' '}
              <img
                src={CloseSmallIcon}
                width={12}
                height={12}
                alt=""
                onClick={(e) => handleDeleteStatusSelected(e, status)}
              />
            </Pill>
          ))
        : ''}
      <ArrowDownIcon
        color="var(--ds-c-grey-dark)"
        size={{ width: 14, height: 10 }}
        styles={{
          transform: isShowDropdown && 'rotate(180deg)',
        }}
      />
    </>
  );
  const renderDropdownMenuChildren = () => (
    <>
      <DropdownSearchInput
        name={FILTER_STATUS_SEARCH_NAME}
        value={searchValue || ''}
        placeholder={t('form_placeholder_type_to_search')}
        onChange={handleSearch}
      />
      <DropdownMenuContainer
        style={{ borderRadius: '0 0 8px 8px', paddingBottom: 0 }}
      >
        {renderDropdownItems(isSearchInvalid ? [] : statusSearch || status)}
      </DropdownMenuContainer>
    </>
  );
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
        >
          {isSearchInvalid ? (
            <SearchErrorText role="alert">
              {t('form_msg_search_validation')}
            </SearchErrorText>
          ) : (
            <NoItemText>{t('form_text_no_result')}</NoItemText>
          )}
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={item.id}
            eventKey={index}
            onClick={(e) => handleStatusSelected(e, item)}
            style={{ display: 'flex' }}
          >
            <FormInputCheckbox
              isChecked={statusSelected?.some(
                (statusSelected) => statusSelected.code === item.code,
              )}
            />
            {t(TRANSACTION_STATUS_TRANSLATE_REFERENCE[item.name])}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  return (
    <StatusDropdownStyled>
      <Label>{t('label_status')}</Label>
      <DropdownCommon
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        renderDropdownToggleChildren={renderDropdownToggleChildren}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
      />
    </StatusDropdownStyled>
  );
};

const StatusDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Pill = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: #ffffff;

  padding: 4px 8px;
  min-width: fit-content;
  height: 24px;
  background: var(--ds-c-blue);
  border-radius: 100px;

  &:last-child {
    margin-right: 0px;
  }

  & img {
    margin-top: -2px;
    margin-left: 2px;
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;
const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-black);

  margin-bottom: 4px;
`;

const SearchErrorText = styled(NoItemText)`
  color: var(--ds-c-red);
`;

export default CustomFilterStatusDropdown;
